import Seo from "@components/Seo";
import { graphql, PageProps } from "gatsby";
import React from "react";
import Main from "@components/Layout/Main";

import Colors from "@components/ProductsPage/Colors";
import NonColors from "@components/ProductsPage/Non-colors";
import SetSlider from "@components/ProductsPage/SetSlider";
import Info from "@components/ProductsPage/Info";
import FeaturedContent from "@components/FeaturedContent";
import PageSection from "@components/PageSection";
import SetTiles from "@components/ProductsPage/SetTiles";
import { Brand } from "@typed/categories";

const ProductGroupPage = ({ data: { pageProductGroup }, pageContext }: PageProps<Queries.ProductGroupPageQuery, { brand?: Brand }>) => {
  if (!pageProductGroup) return null;

  const {
    title,
    description,
    show_sets_tiled,
    featuredImage,
    featuredImageMobile,
    featured_video,
    vimeo_video_url,
    product_group: {
      id: productGroupId,
      products: prodsOrNull,
      category,
      brand: { slug: brandSlug, code: brandCode },
    },
    featured_videos,
    featured_content,
  } = pageProductGroup;

  const videos = [...featured_videos.flatMap((v) => (v ? [v] : []))];

  const resources = [...category.flatMap((c) => c.product_content?.flatMap((pc) => (pc ? [pc] : [])))];
  const articles = [...featured_content.flatMap((v) => (v ? [v] : []))];

  const techsheets = resources.filter((r) => r.content_type?.slug_base !== "external-reference");
  const techSheetList = techsheets.map((a) => ({
    id: a.id,
    title: a.title ?? "",
    description: a.description ?? "",
    image: a.featuredImage?.childImageSharp?.gatsbyImageData,
    slug: `/resources/${a.slug}`, // For FlatList
    type: a.content_type?.slug_base ?? undefined, // For ResourceList
  }));

  const products: Queries.ProductFragment[] = prodsOrNull.flatMap((f) => (f ? [f] : []));
  const colors = products?.filter((p) => p.products_colors != null) ?? [];

  const sets = products?.filter((p) => p?.products_sets) ?? [];

  const noncolors = products?.filter((p) => !p.products_colors && !p.products_sets) ?? [];

  const defaultCategory = [...category].sort((c) => c.categorytype.group_sort)?.[0];
  const defaultCategorySlug = defaultCategory ? `&${defaultCategory.categorytype.group_slug}=${defaultCategory.slug}` : "";

  const hasFeaturedVideo = featured_video?.embed || vimeo_video_url;
  const vimeoId = featured_video?.embed ?? vimeo_video_url?.split("/").pop() ?? "";

  return (
    <Main
      brand={pageContext.brand}
      heroImage={featuredImage?.childImageSharp?.gatsbyImageData}
      heroImageMobile={featuredImageMobile?.childImageSharp?.gatsbyImageData}
      heroImageAlt={title ?? ""}
      title={title ?? ""}
    >
      <section className="mt-1 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="flex flex-col">
          {description && (
            // eslint-disable-next-line react/no-danger
            <article className="prose p-2" dangerouslySetInnerHTML={{ __html: description }} />
          )}
        </div>

        {hasFeaturedVideo && (
          <>
            <div style={{ padding: "56.33% 0 0 0", position: "relative" }}>
              <iframe
                src={`https://player.vimeo.com/video/${vimeoId}?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479`}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                title={`${title} featured video`}
              />
            </div>
            <script src="https://player.vimeo.com/api/player.js" />
          </>
        )}
      </section>

      {colors.length > 1 && brandCode && (
        <Colors colors={colors} displayedColorLineKey={productGroupId} brandCode={brandCode} showcolorLineFilter />
      )}

      {noncolors.length > 1 && <NonColors noncolors={noncolors} />}

      {sets.length > 1 &&
        (show_sets_tiled ? (
          <SetTiles sets={sets} pageGroupKey={productGroupId} tileMaxWidth={brandCode === "pp" ? 200 : 325} />
        ) : (
          <SetSlider sets={sets} />
        ))}

      {(articles.length > 0 || videos.length > 0) && (
        <PageSection sectionName="Featured Content">
          <FeaturedContent
            featured_content={articles}
            more_content_slug={`/resources?brand=${brandSlug}${defaultCategory ? `&resource-type=external-reference${defaultCategorySlug}` : ""}`}
            featured_videos={videos}
            more_videos_slug={`/resources?brand=${brandSlug}&resource-type=video${defaultCategory ? defaultCategorySlug : ""}`}
          />
        </PageSection>
      )}

      {techSheetList && techSheetList.length > 0 && <Info techSheetList={techSheetList} />}
    </Main>
  );
};

export const Head = ({ data: { pageProductGroup } }: { data: Queries.ProductGroupPageQuery }) => {
  const pageTitle = pageProductGroup?.page_metadata?.title ?? pageProductGroup?.title ?? "Product Page";

  return <Seo title={pageTitle} description={pageProductGroup?.page_metadata?.description ?? undefined} />;
};

export default ProductGroupPage;

export const query = graphql`
  query ProductGroupPage($id: String) {
    pageProductGroup(id: { eq: $id }) {
      description
      title
      show_sets_tiled
      id
      vimeo_video_url
      featured_video {
        embed
      }
      featuredImage {
        childImageSharp {
          gatsbyImageData
        }
      }
      featuredImageMobile {
        childImageSharp {
          gatsbyImageData
        }
      }
      page_metadata {
        title
        description
      }
      product_group {
        id
        slug
        brand {
          slug
          code
        }
        products {
          ...Product
        }

        category {
          name
          slug
          categorytype {
            group_name
            group_slug
            group_sort
          }
          product_content {
            ...ProductContent
          }
        }
      }
      featured_content {
        ...ProductContent
      }
      featured_videos {
        ...Video
      }
    }
  }
`;
